import { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

export default function AllergiesAlerts({ data, onChange }) {
  const [allergies, setAllergies] = useState(data.allergies || []);
  const [alerts, setAlerts] = useState(data.alerts || []);

  useEffect(() => {
    setAllergies(data.allergies || []);
    setAlerts(data.alerts || []);
  }, [data.allergies, data.alerts]);

  useEffect(() => {
    onChange({
      allergies,
      alerts,
    });
  }, [allergies, alerts, onChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: '#dbeafe',
            borderBottom: '1px solid #60A5FA',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningAmberOutlinedIcon />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Allergies and Alerts</Typography>
            <Typography variant="body2" color="text.secondary">
              Record key safety information that should be clearly visible on the patient record.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                Allergies
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Type an allergy and press Enter to add it.
              </Typography>

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={allergies}
                onChange={(event, newValue) => setAllergies(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add allergy"
                    placeholder="e.g. Penicillin, peanuts, latex"
                    fullWidth
                  />
                )}
              />

              {allergies.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  No allergies added yet.
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                Alerts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Add important warnings or flags that should be visible to participants.
              </Typography>

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={alerts}
                onChange={(event, newValue) => setAlerts(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add alert"
                    placeholder="e.g. Difficult airway, safeguarding concern"
                    fullWidth
                  />
                )}
              />

              {alerts.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  No alerts added yet.
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}